import React, { useState } from 'react';
import ClientForm from './ClientForm';
import './ClientFormStyles.css';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Api from '../../services/Api';
import { useTranslation } from 'react-i18next';
import { useCountry } from '../../contexts/CountryContext';
import { formatCurrency } from '../../config/countries';
import { motion } from 'framer-motion';

/**
 * Container para o formulário do cliente que gerencia o estado de envio
 * e a comunicação com o backend
 */
import {
  CheckCircle,
  Clock,
  Lock,
  Zap,
  ArrowRight,
  Shield,
  User,
  Heart,
  Brain,
  BarChart3,
  CreditCard,
  Search,
  Star
} from 'lucide-react';

import reportBlur from '../../assets/images/report-blur.png';

/**
 * Container para o formulário do cliente que gerencia o estado de envio
 * e a comunicação com o backend
 */
const ClientFormContainer = () => {
  const { t } = useTranslation();
  const { selectedCountry, countryConfig } = useCountry();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [callInfo, setCallInfo] = useState({});

  // Simulação de Score Detectado (Gatilho de curiosidade)
  const [detectedScore, setDetectedScore] = useState(null);

  // Função para lidar com o envio do formulário
  const handleSubmit = async (formData) => {
    setIsLoading(true);

    try {
      // Enviar os dados para o backend
      const response = await Api.post('/analysis/create', formData);

      // Verificar se a requisição foi bem-sucedida
      if (response?.message) {
        //toast.success(response.message);
        setIsSubmitted(true);
        setCallInfo({
          email: response?.clientEmail,
          callId: response.callId
        });
        // Definir um score aleatório para curiosidade
        setDetectedScore(Math.floor(Math.random() * (92 - 68 + 1)) + 68);
      } else {
        throw new Error('Erro ao enviar os dados.');
      }
    } catch (error) {
      console.error('Erro ao enviar o formulário:', error);

      // Exibir mensagem de erro apropriada
      if (error.response) {
        // O servidor respondeu com um status de erro
        const errorMessage = error.response.data.message || 'Ocorreu um erro ao processar sua solicitação.';
        toast.error(errorMessage);
      } else if (error.request) {
        // A requisição foi feita mas não houve resposta
        toast.error('Não foi possível conectar ao servidor. Por favor, tente novamente mais tarde.');
      } else {
        // Erro na configuração da requisição
        toast.error('Ocorreu um erro ao enviar seus dados. Por favor, tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Função para redirecionar para o checkout do Stripe (cupom: &prefilled_promo_code=30OFF)
  const handlePayment = () => {
    window.open(`https://buy.stripe.com/fZu8wP7kJafj6eX6lPbMQ06?prefilled_email=${callInfo?.email}&client_reference_id=${callInfo?.callId}`, "_self");
  };

  if (isSubmitted) {
    return (
      <div className="cf-container cf-success-page">
        <motion.div
          className="cf-success-wrapper"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
        >

          {/* ── Status stepper ───────────────────────────────── */}
          <div className="cf-success-stepper">
            <div className="cf-ss-step done">
              <div className="cf-ss-circle"><CheckCircle size={16} /></div>
              <span>Envio</span>
            </div>
            <div className="cf-ss-line done" />
            <div className="cf-ss-step active">
              <div className="cf-ss-circle"><Zap size={16} className="cf-ss-pulse" /></div>
              <span>Processando</span>
            </div>
            <div className="cf-ss-line" />
            <div className="cf-ss-step locked">
              <div className="cf-ss-circle"><Lock size={16} /></div>
              <span>Resultado</span>
            </div>
          </div>

          {/* ── Hero header ──────────────────────────────────── */}
          <div className="cf-success-hero">
            <div className="cf-success-hero-icon">
              <Zap size={28} />
            </div>
            <h1 className="cf-success-title">Sua análise está quase pronta!</h1>
            <p className="cf-success-subtitle">
              Imagens processadas com sucesso. Libere o acesso ao seu relatório completo agora.
            </p>
          </div>

          {/* ── Payment card ─────────────────────────────────── */}
          <div className="cf-success-payment-card">
            <div className="cf-spc-top">
              <span className="cf-spc-label">Libere sua análise completa por apenas</span>
              <div className="cf-spc-price">R$ 19,90</div>
              <span className="cf-spc-badge">Pagamento Único • Sem assinatura</span>
            </div>

            <button className="cf-spc-cta" onClick={handlePayment}>
              EFETUAR PAGAMENTO <ArrowRight size={20} />
            </button>

            <div className="cf-spc-trust">
              <span><Lock size={13} /> Pagamento seguro</span>
              <span><Zap size={13} /> Liberação imediata</span>
              <span><Shield size={13} /> Dados protegidos</span>
            </div>
          </div>

          {/* ── What you unlock ──────────────────────────────── */}
          <div className="cf-success-card">
            <h3 className="cf-success-card-title">
              <Star size={16} /> O que você vai receber agora:
            </h3>
            <ul className="cf-unlock-list">
              <li><BarChart3 size={15} /><span>Uma análise completa, detalhada e didática</span></li>
              <li><User size={15} /><span>Seu perfil mental, emocional e comportamental</span></li>
              <li><Heart size={15} /><span>Predisposições encontradas em cada sistema do corpo</span></li>
              <li><Shield size={15} /><span>Explicação de cada sinal encontrado</span></li>
              <li><Star size={15} /><span>Recomendações personalizadas</span></li>
              <li><Clock size={15} /><span>Sugestões de rotinas e hábitos</span></li>
            </ul>
          </div>

          {/* ── Blurred report preview ───────────────────────── */}
          <div className="cf-success-card cf-success-preview-card">
            <div className="cf-success-card-title">
              <BarChart3 size={16} />
              <span>Preview da Análise</span>
              <span className="cf-preview-locked-badge"><Lock size={11} /> Bloqueado</span>
            </div>
            <div className="cf-preview-img-wrap">
              <img src={reportBlur} alt="Preview do relatório" className="cf-preview-img" />
              <div className="cf-preview-overlay">
                <Lock size={22} />
                <span>Conteúdo bloqueado</span>
                <p>Efetue o pagamento para acessar seu relatório completo</p>
              </div>
            </div>
          </div>

          {/* ── Curiosity / tension trigger ──────────────────── */}
          <div className="cf-success-alert">
            <div className="cf-success-alert-icon">⚡</div>
            <div>
              <p>Nossa pré-análise já identificou sinais relevantes na sua íris.</p>
              <p className="cf-alert-cta-text">Finalize o pagamento para acessar sua análise completa.</p>
            </div>
          </div>

          {/* ── Second CTA ───────────────────────────────────── */}
          <button className="cf-spc-cta" onClick={handlePayment}>
            VER ANÁLISE COMPLETA <ArrowRight size={20} />
          </button>

        </motion.div>

        {/* Logo */}
        <div className="cf-logo-wrap">
          <img
            src="https://storage.googleapis.com/e-iris-businesses/621b8185e0b4ed2240ce0f4b/logo.png"
            alt="e-Iris Logo"
            className="cf-logo"
          />
        </div>

        <ToastContainer position="top-right" autoClose={5000} />
      </div>
    );
  }

  // Renderizar o formulário
  return (
    <>
      <ClientForm onSubmit={handleSubmit} isLoading={isLoading} />
      <ToastContainer position="top-right" autoClose={5000} />
    </>
  );
};

export default ClientFormContainer;
